/* eslint-disable react-hooks/exhaustive-deps */
import { Badge, CircularProgress, Container, IconButton, Stack, Typography } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { FC, Fragment, useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { DataExportLauncher, Icon } from '../../components';
import { BottomNavigationAction } from '../../components/bottom-navigation';
import { useResponsiveText } from '../../hooks';
import { innerText } from '../../util';
import { ListLayoutMobile } from './index';
import { InternalProps } from './view.types';

export const ListPageViewMobile: FC<InternalProps> = ({
  actions,
  appliedFiltersComponents,
  clearAllFiltersComponent,
  dataExportConfig,
  dataExportOverrides,
  extraActions,
  filterDisabled,
  filterDrawerComponent,
  filtersLength,
  lifecycleTemplate,
  listContent,
  openFilter,
  paginationComponent,
  scrollRef,
  search,
  searchOpen,
  searchRemoved,
  setFilterOpen,
  setSearchOpen,
  showFilterButton,
  showExtraActions,
  stakeholderRole,
  subtitleComponent,
  tableContent,
  ...props
}): JSX.Element => {
  const { t } = useTranslation(['common']);

  const searchText = t('common:component.search.labels.label');
  const h5Styles = useResponsiveText('1.5rem', innerText(searchText).length, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  const mobileSearchComponent = useMemo(() => {
    if (searchRemoved) return <></>;
    return (
      <Container>
        <Stack direction="column" spacing={1} py={2.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              variant="h5"
              style={{
                fontSize: h5Styles.fontSize,
                lineHeight: h5Styles.lineHeight,
                wordBreak: h5Styles.wordBreak,
                whiteSpace: h5Styles.whiteSpace,
                overflow: h5Styles.overflow,
                textOverflow: h5Styles.textOverflow,
              }}
            >
              {searchText}
            </Typography>
            <IconButton
              onClick={() => setSearchOpen(false)}
            >
              <Icon data-testid="close-icon" icon="CloseIcon" />
            </IconButton>
          </Box>
          <Box sx={{ width: 'inherit' }}>
            {search}
          </Box>
        </Stack>
      </Container>
    );
  }, [search, searchRemoved, setSearchOpen]);

  const filterIcon = (
    <Badge content={filtersLength}>
      <Icon icon="FilterIcon" />
    </Badge>
  );

  const dataExportComponent = dataExportConfig?.isEnabled ? (
    <DataExportLauncher
      key="data-export-launcher"
      dataExportSettings={dataExportConfig.settings}
      dataExportOverrides={dataExportOverrides}
      isMobile
    />
  ) : null;

  const actionComponents = useMemo<JSX.Element[]>(
    () => [
      !searchRemoved && <BottomNavigationAction key={'search'} label={t('common:component.search.labels.label')} icon={<Icon icon="SearchIcon" data-testid="search-icon" />} onClick={() => setSearchOpen(true)} data-testid="search-button" />,
      showFilterButton
      && <BottomNavigationAction
        data-testid='filters-button'
        key={'filter'}
        icon={filterIcon}
        label={t('common:terms.filter')}
        onClick={() => setFilterOpen(true)}
      />,
      ...(showExtraActions
        ? (Array.isArray(extraActions) ? extraActions : [extraActions])
        : []),
      ...(actions?.length ? actions.map((action, i) => {
        if (action.buttonNode) {
          return <Fragment key={i}>{action.buttonNode}</Fragment>;
        }
        return (
          <BottomNavigationAction
            key={i}
            disabled={action.disabled || action.isLoading}
            label={action?.shortLabel || action.label}
            href={action.href}
            icon={action.isLoading ? <CircularProgress color="inherit" size="1.5rem" /> : action.icon}
            onClick={action.onClick}
            value={action.shortLabel}
            data-testid={action.testId ?? 'action-button'}
          />
        );
      }) : []),
      dataExportComponent,
    ].filter(Boolean) as JSX.Element[],
    [actions, extraActions, filtersLength, searchRemoved, setFilterOpen, t, showFilterButton],
  );

  return (
    <ListLayoutMobile
      data-testid={props['data-testid']}
      actions={actionComponents}
      clearAllFiltersButton={clearAllFiltersComponent}
      content={listContent ?? tableContent ?? <></>}
      filterContent={filterDrawerComponent}
      filters={appliedFiltersComponents}
      pagination={listContent && paginationComponent}
      scrollRef={scrollRef}
      search={mobileSearchComponent}
      searchOpen={searchOpen}
      setSearchOpen={setSearchOpen}
      showFilters={openFilter}
      subtitle={subtitleComponent}
    />
  );
};
